import * as Actions from '../../../Modules/CmsActions'
import * as Permission from '../../../Services/Permission'
import type { Permission as PermissionType, PostType } from '../../../Types'

interface ISideMenuState {
  currentMenu: 'post'
  postMenuGroups: IMenuItemGroup[]
  active: null | string
  visibles: {
    globalMenu: IMenu
  }
}

interface IMenu {
  chat?: boolean
  contents?: boolean
  document?: boolean
  lead?: boolean
  site?: boolean
  list?: boolean
}

export interface IMenuItemGroup {
  name?: string
  title: string
  link: string
}

const initialState: ISideMenuState = {
  currentMenu: 'post',
  postMenuGroups: [],
  active: null,
  visibles: {
    globalMenu: {
      contents: false,
      document: false,
      lead: false,
      site: false,
    },
  },
}

const UISideMenuReducer = (state = initialState, action) => {
  const { payload } = action

  switch (action.type) {
    case Actions.LOADED_INITIAL_VALUES:
      return {
        ...state,
        visibles: updateVisibles(payload.permissions, []),
      }
    case Actions.SELECT_SITE:
    case Actions.LOADED_INITIAL_SITE_VALUES:
      return {
        ...state,
        postMenuGroups: createPostMenu(payload.postTypes, payload.permissions),
        visibles: updateVisibles(payload.permissions, payload.site.id),
      }

    case Actions.FINISH_CREATE_POST_TYPE:
      return {
        ...state,
        postMenuGroups: [
          ...state.postMenuGroups,
          {
            name: payload.postType.name,
            title: payload.postType.display_name,
            link: `posts/${payload.postType.name}`,
          },
        ],
      }

    case Actions.CHANGE_SIDE_MENU:
      return {
        ...state,
        currentMenu: action.payload.currentMenu,
      }

    case Actions.OPEN_SIDE_MENU:
      return { ...state, active: payload.index }

    case Actions.LOGOUT:
      return initialState

    default:
      return state
  }
}

function createPostMenu(
  postTypes: PostType[],
  permissions: PermissionType[],
): IMenuItemGroup[] {
  return postTypes
    .filter((postType) => hasSomePostPermission(permissions, postType.id))
    .map((postType) => {
      return {
        name: postType.name,
        title: postType.display_name,
        link: `posts/${postType.name}`,
      }
    })
}

function updateVisibles(permissions, siteId) {
  return {
    globalMenu: {
      chat: hasSomeWorkspacePermission(permissions, ['chat_support'], 'admin'),
    },
    homeMenu: {
      lead: hasSomeWorkspacePermission(permissions, ['lead']),
      list: hasSomeWorkspacePermission(permissions, ['list']),
      user: hasSomeWorkspacePermission(permissions, ['user']),
      document: hasSomeWorkspacePermission(permissions, ['document']),
    },
    sideMenu: {
      page: {
        view: Permission.hasPermission(siteId, permissions, 'view', 'pages'),
        create: Permission.hasPermission(
          siteId,
          permissions,
          'create',
          'pages',
        ),
      },
      file: {
        view: Permission.hasPermission(siteId, permissions, 'view', 'file'),
        create: Permission.hasPermission(siteId, permissions, 'create', 'file'),
      },
      cta: {
        view: Permission.hasPermission(siteId, permissions, 'view', 'cta'),
        create: Permission.hasPermission(siteId, permissions, 'create', 'cta'),
      },
      ctaArea: {
        view: Permission.hasPermission(siteId, permissions, 'view', 'cta_area'),
      },
      site: {
        view: Permission.hasPermission(siteId, permissions, 'view', 'site'),
        update: Permission.hasPermission(siteId, permissions, 'update', 'site'),
      },
      navigation: {
        admin: Permission.hasPermission(
          siteId,
          permissions,
          'admin',
          'navigation',
        ),
      },
      integration: {
        admin: Permission.hasPermission(
          siteId,
          permissions,
          'admin',
          'integration',
        ),
      },
      redirection: {
        admin: Permission.hasPermission(
          siteId,
          permissions,
          'admin',
          'redirection',
        ),
      },
      rss: {
        admin: Permission.hasPermission(siteId, permissions, 'admin', 'rss'),
      },
      cookie: {
        admin: Permission.hasPermission(siteId, permissions, 'admin', 'cookie'),
      },
      form: {
        admin: hasSomeWorkspacePermission(permissions, ['form'], 'admin'),
      },
      postType: {
        admin: Permission.hasPermission(
          siteId,
          permissions,
          'admin',
          'post_type',
        ),
        create: Permission.hasPermission(
          siteId,
          permissions,
          'admin',
          'lg_template',
        ),
      },
      property: {
        edit: Permission.hasPermission(siteId, permissions, 'edit', 'property'),
        input: Permission.hasPermission(
          siteId,
          permissions,
          'input',
          'property',
        ),
        view: Permission.hasPermission(siteId, permissions, 'view', 'property'),
      },
    },
  }
}

function hasSomePostPermission(permissions, id) {
  return (
    Permission.hasPostPermission(permissions, 'view', id) ||
    Permission.hasPostPermission(permissions, 'admin', id)
  )
}

function hasSomeResourcePermission(permissions, resources, action = 'view') {
  return resources.some((resource) =>
    Permission.hasHomePermission(permissions, action, resource),
  )
}

function hasSomeWorkspacePermission(permissions, resources, action = 'view') {
  return resources.some((resource) =>
    Permission.hasWorkspacePermission(permissions, action, resource),
  )
}

export default UISideMenuReducer
